import { Button, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import React, { useRef, useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { ReportLookerData } from 'services/dataService';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';

const REPORT_TYPES = [
    { key: 'academic', label: 'Acadêmico', update: (id) => ReportLookerData.updateCompanyAcademic(id) },
    { key: 'trails', label: 'Trilhas', update: (id) => ReportLookerData.updateCompanyTrails(id) },
    { key: 'users', label: 'Usuários', update: (id) => ReportLookerData.updateCompanyUsers(id) },
];

const ModalLookerCompany = ({ setOpen }) => {
    const tableRef = useRef();
    const inFlightKeysRef = useRef(new Set());
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [rows, setRows] = useState([]);
    const [disabledActions, setDisabledActions] = useState([]);
    const [loadingAll, setLoadingAll] = useState(false);

    const fetchCompanies = async () => {
        try {
            const { data } = await ReportLookerData.getCompanyReports();
            const sortedCompanies = (data?.companies || [])
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }));
            setCompanies(sortedCompanies);
        } catch (err) {
            toast.error('Erro ao carregar empresas configuradas para o looker personalizado');
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        const companyData = selectedCompany
            ? companies.find((company) => company.id_company === selectedCompany)
            : null;

        const availableReports = REPORT_TYPES
            .filter((reportType) => !!companyData?.reports?.[reportType.key])
            .map((reportType) => ({
                ...reportType,
                link: companyData.reports[reportType.key]?.link,
                update_at: null,
                status: 'idle',
                statusMessage: '',
            }));

        setRows(availableReports);
    }, [selectedCompany, companies]);

    const formatDateTime = () => {
        const now = new Date();
        return now.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const disableRowTemporarily = (key) => {
        setDisabledActions((prev) => [...prev, key]);
        setTimeout(() => {
            setDisabledActions((prev) => prev.filter((i) => i !== key));
        }, 10 * 60 * 1000);
    };

    const handleVisibilityClick = (rowData) => {
        if (rowData?.link) {
            window.open(rowData.link, '_blank', 'noopener,noreferrer');
        } else {
            toast.warn(`Nenhum link disponível para ${rowData.label}`);
        }
    };

    const updateRowStatus = (key, changes) => {
        setRows((prevRows) =>
            prevRows.map((row) => (row.key === key ? { ...row, ...changes } : row))
        );
    };

    const handleIconClick = async (rowData) => {
        if (inFlightKeysRef.current.has(rowData.key)) {
            return;
        }
        inFlightKeysRef.current.add(rowData.key);

        disableRowTemporarily(rowData.key);

        updateRowStatus(rowData.key, {
            status: 'loading',
            statusMessage: 'Atualizando...',
        });

        try {
            const response = await rowData.update(selectedCompany);
            const lastUpdate = response?.data?.last_update || formatDateTime();

            updateRowStatus(rowData.key, {
                update_at: lastUpdate,
                status: 'success',
                statusMessage: `Atualização realizada com sucesso às ${lastUpdate}`,
            });

            toast.success(`${rowData.label} atualizado com sucesso! 🚀`);
        } catch (err) {
            const isAlreadyRunning = err.response?.status === 409;
            const errorMessage = isAlreadyRunning
                ? err.response?.data?.error || `${rowData.label} já está sendo atualizado, aguarde a conclusão.`
                : err.response?.status === 403
                    ? `Sem permissão para atualizar ${rowData.label}`
                    : `Erro ao atualizar ${rowData.label}`;

            updateRowStatus(rowData.key, {
                status: 'error',
                statusMessage: isAlreadyRunning
                    ? 'Atualização já em andamento. Aguarde a conclusão.'
                    : 'Falha na atualização. Tente novamente.',
            });

            toast.error(errorMessage);
        } finally {
            inFlightKeysRef.current.delete(rowData.key);
        }
    };

    // "Atualizar Todos" desativado por ora — não será utilizado no momento.
    // const handleUpdateAll = async () => {
    //     setLoadingAll(true);
    //     for (const row of rows) {
    //         await handleIconClick(row);
    //     }
    //     setLoadingAll(false);
    // };

    return (
        <Dialog open={setOpen}>
            <DialogTitle style={{ fontSize: '22px', textAlign: 'center' }}>
                Looker Personalizado (Cliente)
            </DialogTitle>
            <DialogContent style={{ textAlign: 'center', minWidth: '450px' }}>
                <FormControl fullWidth style={{ marginBottom: '20px', textAlign: 'left' }}>
                    <InputLabel>Empresa</InputLabel>
                    <Select
                        fullWidth
                        value={selectedCompany}
                        onChange={(event) => setSelectedCompany(event.target.value)}
                    >
                        {companies.map((company) => (
                            <MenuItem key={company.id_company} value={company.id_company}>
                                {company.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedCompany && rows.length === 0 && (
                    <p>Nenhum relatório personalizado configurado para esta empresa.</p>
                )}

                {rows.length > 0 && (
                    <>
                        {/* "Atualizar Todos" desativado por ora — não será utilizado no momento.
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleUpdateAll}
                            disabled={loadingAll || disabledActions.length > 0}
                            style={{ marginBottom: '20px' }}
                        >
                            {loadingAll ? 'Atualizando...' : 'Atualizar Todos'}
                        </Button>
                        */}

                        <MaterialTable
                            ref={tableRef}
                            columns={[
                                { title: 'Relatório', field: 'label' },
                                {
                                    title: 'Status',
                                    field: 'statusMessage',
                                    render: (rowData) => (
                                        <span
                                            style={{
                                                color:
                                                    rowData.status === 'success'
                                                        ? '#2e7d32'
                                                        : rowData.status === 'error'
                                                            ? '#c62828'
                                                            : rowData.status === 'loading'
                                                                ? '#1565c0'
                                                                : '#757575',
                                                fontWeight: rowData.status === 'idle' ? 'normal' : 'bold',
                                            }}
                                        >
                                            {rowData.statusMessage || 'Aguardando atualização'}
                                        </span>
                                    ),
                                },
                            ]}
                            data={rows}
                            actions={[
                                (rowData) => ({
                                    icon: 'sync',
                                    tooltip: disabledActions.includes(rowData.key)
                                        ? 'Aguarde para atualizar novamente'
                                        : 'Executar Requisição (tempo estimado ~1min)',
                                    onClick: (event, rowData) => handleIconClick(rowData),
                                    disabled: disabledActions.includes(rowData.key),
                                    iconProps: {
                                        color: disabledActions.includes(rowData.key)
                                            ? 'disabled'
                                            : 'primary',
                                    },
                                }),
                                {
                                    icon: 'visibility',
                                    tooltip: 'Abrir planilha',
                                    onClick: (event, rowData) => handleVisibilityClick(rowData),
                                    iconProps: { color: 'primary' },
                                },
                            ]}
                            options={{
                                actionsColumnIndex: -1,
                                showTitle: false,
                                search: false,
                                paging: false,
                                emptyRowsWhenPaging: false,
                                rowStyle: (rowData) => ({
                                    backgroundColor:
                                        rowData.tableData.id % 2 === 0 ? '#F6F9FC' : '#FFFF',
                                }),
                            }}
                            localization={{
                                header: {
                                    actions: 'Ações',
                                },
                            }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpen(false)}
                    style={{ padding: '10px 50px' }}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalLookerCompany;
